import React from "react";
import { Terminal, Laptop, Settings2, Copy, Check } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useLocalRunCommandQuery } from "../api/jupyterManagementApi";
import { toast } from "sonner";
import { cn } from "../lib/utils";

interface LocalRunButtonProps {
  projectId: string;
  className?: string;
  variant?: "outline" | "default" | "ghost" | "link" | "destructive" | "secondary" | "bits";
}

export function LocalRunButton({ projectId, className, variant = "outline" }: LocalRunButtonProps) {
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const { data, isLoading } = useLocalRunCommandQuery(projectId, open);

  const copyCommand = () => {
    if (data?.command) {
      navigator.clipboard.writeText(data.command);
      setCopied(true);
      toast.success("Command copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <Button
        variant={variant as any}
        className={cn(
          "w-full h-full p-4 border-bits-blue/30 bg-bits-blue/5 hover:bg-bits-blue/10 text-bits-blue font-semibold group transition-all duration-300",
          className
        )}
        onClick={() => setOpen(true)}
      >
        <Terminal className="h-5 w-5 mr-3 group-hover:scale-110 transition-transform" />
        <div className="flex flex-col items-start">
          <span className="text-sm">Step 1: Run Locally</span>
          <span className="text-[10px] opacity-70 font-normal uppercase tracking-wider">Setup Local Agent</span>
        </div>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-bits-blue flex items-center gap-2">
              <Laptop className="h-5 w-5" />
              Setup Local Developer Agent
            </DialogTitle>
            <DialogDescription>
              Follow these steps to run the project environment on your own machine. This ensures zero latency and offline availability.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Docker Prerequisite Alert */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
              <div className="bg-amber-100 p-2 rounded-full">
                <Settings2 className="h-4 w-4 text-amber-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-900">Docker Required</p>
                <p className="text-xs text-amber-800">You must have <b>Docker Desktop</b> or <b>Docker Engine</b> running on your computer to use this feature.</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">Run this command in your terminal:</p>
                {data?.command && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={copyCommand}
                    className="h-8 text-xs flex gap-2"
                  >
                    {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                    {copied ? "Copied" : "Copy Command"}
                  </Button>
                )}
              </div>
              
              <div className="relative group">
                <div className="bg-slate-950 rounded-lg p-4 font-mono text-xs text-slate-200 overflow-x-auto max-h-[250px] whitespace-pre-wrap break-all border border-slate-800">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-400"></div>
                    </div>
                  ) : data?.command ? (
                    data.command
                  ) : (
                    <span className="text-slate-500 italic">Failed to generate command. Please close and try again.</span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <h4 className="text-sm font-semibold text-blue-900 mb-1">What happens next?</h4>
              <ol className="text-xs text-blue-800 space-y-2 list-decimal ml-4">
                <li>The agent starts a secure tunnel to your local machine.</li>
                <li>Once connected, the <b>Step 2: Launch Sandbox</b> button will become active.</li>
                <li>Click it to open the Jupyter environment running on your machine.</li>
              </ol>
            </div>
            
            <div className="flex justify-end">
              <Button onClick={() => setOpen(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
